import { useState, useCallback, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Share2, Check, Link2 } from "lucide-react";
import PageHero from "./PageHero";
import KennelSEO, { breadcrumbSchema } from "./KennelSEO";

const gallerySchema = {
  "@context": "https://schema.org",
  "@type": "ImageGallery",
  "name": "Galeria zdjęć — Wolfe vom Katzenberg",
  "description": "Galeria zdjęć owczarków niemieckich z hodowli Wolfe vom Katzenberg.",
  "url": "https://wolfevomkatzenberg.pl/galeria"
};

const API = process.env.REACT_APP_BACKEND_URL + "/api/kennel";

const KennelGallery = () => {
  useOutletContext();
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [copied, setCopied] = useState(false);
  const [failedImages, setFailedImages] = useState(new Set());
  const [galleryPhotos, setGalleryPhotos] = useState([]);

  useEffect(() => {
    fetch(`${API}/gallery`).then(r => r.json()).then(data => setGalleryPhotos(data)).catch(() => {});
  }, []);

  const visiblePhotos = galleryPhotos.filter(img => !failedImages.has(img.src));

  const handleImageError = (src) => {
    setFailedImages(prev => new Set([...prev, src]));
  };

  const handleShare = async (e, img) => {
    e.stopPropagation();
    const shareUrl = `${window.location.origin}${img.src}`;
    if (navigator.share) {
      try { await navigator.share({ title: img.label, url: shareUrl }); } catch {}
    } else {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const navigate = useCallback((dir) => {
    setLightboxIdx(prev => {
      if (prev === null) return null;
      if (dir === "next") return (prev + 1) % visiblePhotos.length;
      return (prev - 1 + visiblePhotos.length) % visiblePhotos.length;
    });
  }, [visiblePhotos.length]);

  useEffect(() => {
    const handler = (e) => {
      if (lightboxIdx === null) return;
      if (e.key === "Escape") setLightboxIdx(null);
      if (e.key === "ArrowRight") navigate("next");
      if (e.key === "ArrowLeft") navigate("prev");
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightboxIdx, navigate]);

  return (
    <div>
      <KennelSEO
        title="Galeria zdjęć | Wolfe vom Katzenberg — Hodowla Owczarka Niemieckiego"
        description="Galeria zdjęć owczarków niemieckich z hodowli Wolfe vom Katzenberg. Zobacz nasze psy hodowlane."
        canonical="https://wolfevomkatzenberg.pl/galeria"
        schema={[gallerySchema, breadcrumbSchema([{ name: "Start", href: "/" }, { name: "Galeria", href: "/galeria" }])]}
      />
      <PageHero
        breadcrumbs={[{ label: "GALERIA" }]}
        label="GALERIA"
        title="Nasze psy takimi, jakie są naprawdę"
        description="Zdjęcia mówią więcej niż słowa. Tu znajdziesz nasze psy w codziennych chwilach — na spacerach, wystawach, w domu i na treningu. Bez retuszu, bez ustawek. Po prostu psy, które kochamy."
        testId="gallery"
      />

      <div className="px-2 sm:px-3 py-6 w-full">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3" data-testid="gallery-grid">
          {visiblePhotos.map(({ src, label }, idx) => (
            <motion.div key={src} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              transition={{ delay: 0.02 + (idx % 12) * 0.02, duration: 0.3 }}
              className="relative aspect-square overflow-hidden cursor-pointer group bg-[#0B0B0D]"
              onClick={() => setLightboxIdx(idx)} data-testid={`gallery-item-${idx}`}
            >
              <img src={src} alt={label} loading="lazy"
                onError={() => handleImageError(src)}
                className="w-full h-full object-contain block transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
              <button onClick={(e) => handleShare(e, { src, label })} data-testid={`gallery-share-${idx}`}
                className="absolute top-3 right-3 w-9 h-9 bg-[#0B0B0D]/50 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-[#D4AF37] text-white/80 hover:text-[#0B0B0D] z-10">
                <Share2 className="w-4 h-4" />
              </button>
            </motion.div>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {lightboxIdx !== null && visiblePhotos[lightboxIdx] && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] bg-[#0B0B0D]/95 backdrop-blur-sm flex items-center justify-center" onClick={() => setLightboxIdx(null)} data-testid="gallery-lightbox"
          >
            <button onClick={() => setLightboxIdx(null)} className="absolute top-6 right-6 z-50 w-12 h-12 bg-[#1A1A1D] border border-[#2A2A2D] flex items-center justify-center text-[#B8B8B8] hover:text-[#F5F5F5]" data-testid="lightbox-close"><X className="w-5 h-5" /></button>
            <button onClick={(e) => { e.stopPropagation(); handleShare(e, visiblePhotos[lightboxIdx]); }} className="absolute top-6 right-20 z-50 w-12 h-12 bg-[#1A1A1D] border border-[#2A2A2D] flex items-center justify-center text-[#B8B8B8] hover:text-[#D4AF37] transition-colors" data-testid="lightbox-share">
              {copied ? <Check className="w-5 h-5 text-[#D4AF37]" /> : <Share2 className="w-5 h-5" />}
            </button>
            <button onClick={(e) => { e.stopPropagation(); navigate("prev"); }} className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-50 w-14 h-14 bg-[#1A1A1D] border border-[#2A2A2D] flex items-center justify-center text-[#B8B8B8] hover:text-[#F5F5F5]" data-testid="lightbox-prev"><ChevronLeft className="w-6 h-6" /></button>
            <button onClick={(e) => { e.stopPropagation(); navigate("next"); }} className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-50 w-14 h-14 bg-[#1A1A1D] border border-[#2A2A2D] flex items-center justify-center text-[#B8B8B8] hover:text-[#F5F5F5]" data-testid="lightbox-next"><ChevronRight className="w-6 h-6" /></button>
            <motion.img key={visiblePhotos[lightboxIdx].src} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} src={visiblePhotos[lightboxIdx].src} alt=""
              className="max-w-[90vw] max-h-[85vh] object-contain" onClick={e => e.stopPropagation()} />
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-xs text-[#B8B8B8]/30 tracking-wider">{lightboxIdx + 1} / {visiblePhotos.length}</div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {copied && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[10000] bg-[#1A1A1D] border border-[#D4AF37]/30 px-6 py-3 flex items-center gap-3" data-testid="share-toast">
            <Link2 className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-[#F5F5F5] text-xs tracking-wider">Link skopiowany</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
export default KennelGallery;
